import React, { useState, useEffect } from "react";
import { Plus, Search, Filter, Building2 } from "lucide-react";
import { format } from "date-fns";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Calendar as CalendarIcon } from "lucide-react";
import { StatusBadge } from "../components/common/StatusBadge";
import { QuickActions } from "../components/common/QuickActions";
import { useAuth } from "../context/AuthContext";
import { apiRequest, formatPhone, formatDateTime, cn } from "../lib/utils";
import { toast } from "sonner";

const getSourceBadge = (sourceType, bookingSource) => {
  if (sourceType === "lead") {
    return (
      <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200">
        New Lead
      </Badge>
    );
  }
  // SDR sources
  switch (bookingSource) {
    case "due":
      return (
        <Badge variant="outline" className="text-xs bg-yellow-50 text-yellow-700 border-yellow-200">
          Due
        </Badge>
      );
    case "overdue":
      return (
        <Badge variant="outline" className="text-xs bg-orange-50 text-orange-700 border-orange-200">
          Overdue
        </Badge>
      );
    case "delayed":
      return (
        <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
          Delayed
        </Badge>
      );
    case "no_show":
      return (
        <Badge variant="outline" className="text-xs bg-red-50 text-red-700 border-red-200">
          No Show
        </Badge>
      );
    default:
      return (
        <Badge variant="outline" className="text-xs">
          {bookingSource || "SDR"}
        </Badge>
      );
  }
};

export const BookingsPage = () => {
  const { user, isAdmin } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [branches, setBranches] = useState([]);
  const [customers, setCustomers] = useState([]);
  
  // Modals
  const [showAddModal, setShowAddModal] = useState(false);
  
  // New booking form
  const [newBooking, setNewBooking] = useState({
    customer_id: "",
    branch_id: "",
    booking_date: null,
    service_type: "",
    amount: "",
    notes: "",
    booking_source: "new_lead",
    source_type: "lead"
  });

  useEffect(() => {
    fetchBookings();
    fetchBranches();
    fetchCustomers();
  }, [statusFilter]);

  const fetchBookings = async () => {
    try {
      let url = "/bookings";
      const params = [];
      if (statusFilter && statusFilter !== "all") {
        params.push(`status=${statusFilter}`);
      }
      // For CRE users, use cre_user_id to filter by their assigned branches
      if (!isAdmin() && user?.id) {
        params.push(`cre_user_id=${user.id}`);
      }
      if (params.length > 0) {
        url += `?${params.join("&")}`;
      }
      const data = await apiRequest(url);
      setBookings(data);
    } catch (error) {
      toast.error("Failed to fetch bookings");
    } finally {
      setLoading(false);
    }
  };

  const fetchBranches = async () => {
    try {
      const data = await apiRequest("/branches");
      setBranches(data);
    } catch (error) {
      console.error("Failed to fetch branches:", error);
    }
  };

  const fetchCustomers = async () => {
    try {
      const data = await apiRequest("/customers");
      setCustomers(data);
    } catch (error) {
      console.error("Failed to fetch customers:", error);
    }
  };

  const handleCustomerSelect = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    if (customer) {
      setNewBooking({ 
        ...newBooking, 
        customer_id: customerId,
        source_type: customer.source_type || "lead",
        booking_source: customer.source_type === "sdr" ? customer.status || "due" : "new_lead",
        branch_id: customer.branch_id || newBooking.branch_id
      });
    }
  };

  const handleAddBooking = async (e) => {
    e.preventDefault();
    if (!newBooking.booking_date) {
      toast.error("Please select a booking date");
      return;
    }
    try {
      await apiRequest("/bookings", {
        method: "POST",
        body: JSON.stringify({
          ...newBooking,
          cre_id: user.id,
          booking_date: newBooking.booking_date.toISOString(),
          amount: newBooking.amount ? parseFloat(newBooking.amount) : null
        })
      });
      toast.success("Booking created successfully");
      setShowAddModal(false);
      setNewBooking({
        customer_id: "",
        branch_id: "",
        booking_date: null,
        service_type: "",
        amount: "",
        notes: "",
        booking_source: "new_lead",
        source_type: "lead"
      });
      fetchBookings();
    } catch (error) {
      toast.error(error.message || "Failed to create booking");
    }
  };

  const handleUpdateStatus = async (bookingId, newStatus) => {
    try {
      await apiRequest(`/bookings/${bookingId}`, {
        method: "PUT",
        body: JSON.stringify({ status: newStatus })
      });
      toast.success("Booking status updated");
      fetchBookings();
    } catch (error) {
      toast.error("Failed to update booking status");
    }
  };

  const filteredBookings = bookings.filter((booking) => {
    const matchesSearch =
      booking.contact_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.contact_phone?.includes(searchTerm) ||
      booking.service_type?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesSource = sourceFilter === "all" || booking.source_type === sourceFilter;
    return matchesSearch && matchesSource;
  });

  const statusOptions = [
    { value: "all", label: "All Statuses" },
    { value: "scheduled", label: "Scheduled" },
    { value: "completed", label: "Completed" },
    { value: "cancelled", label: "Cancelled" },
    { value: "no_show", label: "No Show" }
  ];

  const sourceOptions = [
    { value: "all", label: "All Sources" },
    { value: "lead", label: "New Leads" },
    { value: "sdr", label: "SDR (Servall)" }
  ];

  const getBranchName = (branchId) => {
    const branch = branches.find(b => b.id === branchId);
    return branch?.name || "-";
  };

  return (
    <div className="space-y-6" data-testid="bookings-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-foreground">Bookings</h1>
          <p className="text-muted-foreground">Manage service appointments</p>
        </div>
        <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
          <DialogTrigger asChild>
            <Button data-testid="add-booking-button">
              <Plus className="w-4 h-4 mr-2" />
              New Booking
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle className="font-heading">Create Booking</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleAddBooking} className="space-y-4">
              <div>
                <Label htmlFor="customer">Customer *</Label>
                <Select
                  value={newBooking.customer_id}
                  onValueChange={handleCustomerSelect}
                >
                  <SelectTrigger data-testid="booking-customer-select">
                    <SelectValue placeholder="Select customer" />
                  </SelectTrigger>
                  <SelectContent>
                    {customers.map((customer) => (
                      <SelectItem key={customer.id} value={customer.id}>
                        <div className="flex items-center gap-2">
                          <span>{customer.name} - {formatPhone(customer.phone)}</span>
                          <Badge variant="outline" className="text-xs">
                            {customer.source_type === "lead" ? "Lead" : "SDR"}
                          </Badge>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="branch">Branch *</Label>
                <Select
                  value={newBooking.branch_id}
                  onValueChange={(value) => setNewBooking({ ...newBooking, branch_id: value })}
                >
                  <SelectTrigger data-testid="booking-branch-select">
                    <SelectValue placeholder="Select branch" />
                  </SelectTrigger>
                  <SelectContent>
                    {branches.map((branch) => (
                      <SelectItem key={branch.id} value={branch.id}>
                        {branch.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Booking Date *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !newBooking.booking_date && "text-muted-foreground"
                      )}
                      data-testid="booking-date-trigger"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {newBooking.booking_date
                        ? format(newBooking.booking_date, "dd MMM yyyy")
                        : "Select date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={newBooking.booking_date}
                      onSelect={(date) => setNewBooking({ ...newBooking, booking_date: date })}
                      disabled={(date) => date < new Date()}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div>
                <Label htmlFor="service">Service Type *</Label>
                <Input
                  id="service"
                  value={newBooking.service_type}
                  onChange={(e) => setNewBooking({ ...newBooking, service_type: e.target.value })}
                  placeholder="e.g., Hair Treatment"
                  required
                  data-testid="booking-service-input"
                />
              </div>
              <div>
                <Label htmlFor="amount">Amount</Label>
                <Input
                  id="amount"
                  type="number"
                  value={newBooking.amount}
                  onChange={(e) => setNewBooking({ ...newBooking, amount: e.target.value })}
                  placeholder="0.00"
                  data-testid="booking-amount-input"
                />
              </div>
              <div>
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  value={newBooking.notes}
                  onChange={(e) => setNewBooking({ ...newBooking, notes: e.target.value })}
                  rows={2}
                  data-testid="booking-notes-input"
                />
              </div>
              <div className="flex justify-end gap-3">
                <Button type="button" variant="outline" onClick={() => setShowAddModal(false)}>
                  Cancel
                </Button>
                <Button type="submit" data-testid="save-booking-button">
                  Create Booking
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <Card className="bento-card">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by name, phone, or service..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
                data-testid="booking-search-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-[160px]" data-testid="booking-status-filter">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {statusOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={sourceFilter} onValueChange={setSourceFilter}>
              <SelectTrigger className="w-full sm:w-[160px]" data-testid="booking-source-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {sourceOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Bookings Table */}
      <Card className="bento-card">
        <CardContent className="p-0">
          <div className="data-table-container">
            <Table>
              <TableHeader>
                <TableRow className="table-high-density">
                  <TableHead>Customer</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Branch</TableHead>
                  <TableHead>Service</TableHead>
                  <TableHead>Date/Time</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : filteredBookings.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                      No bookings found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredBookings.map((booking) => (
                    <TableRow key={booking.id} className="table-high-density" data-testid={`booking-row-${booking.id}`}>
                      <TableCell className="font-medium">{booking.contact_name || "-"}</TableCell>
                      <TableCell className="font-mono text-sm">
                        {formatPhone(booking.contact_phone)}
                      </TableCell>
                      <TableCell>
                        {getSourceBadge(booking.source_type, booking.booking_source)}
                      </TableCell>
                      <TableCell className="text-sm">
                        <div className="flex items-center gap-1">
                          <Building2 className="w-3 h-3 text-muted-foreground" />
                          {getBranchName(booking.branch_id)}
                        </div>
                      </TableCell>
                      <TableCell>{booking.service_type}</TableCell>
                      <TableCell className="text-sm font-mono">
                        {formatDateTime(booking.booking_date)}
                      </TableCell>
                      <TableCell>
                        <Select
                          value={booking.status}
                          onValueChange={(value) => handleUpdateStatus(booking.id, value)}
                        >
                          <SelectTrigger className="w-[130px] h-8">
                            <StatusBadge status={booking.status} />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="scheduled">Scheduled</SelectItem>
                            <SelectItem value="completed">Completed</SelectItem>
                            <SelectItem value="cancelled">Cancelled</SelectItem>
                            <SelectItem value="no_show">No Show</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {booking.amount ? `₹${booking.amount.toLocaleString()}` : "-"}
                      </TableCell>
                      <TableCell className="text-right">
                        <QuickActions
                          phone={booking.contact_phone}
                          showHistory={false}
                        />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default BookingsPage;
